import { useMemo } from "react"
import { GeoJsonLayer } from "@deck.gl/layers"
import { PathStyleExtension, PathStyleExtensionProps } from "@deck.gl/extensions"
import { calculateCirclePolygon } from "@nova/calculate-geospatial"
import { ADSB_AREA_RADIUS_METERS } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import { selectAdsbCircleCenter, selectAdsbEnabled } from "features/overview/slice"

const DASHED: [number, number] = [8, 5]

const unwrapAntimeridianRing = (ring: [number, number][]): [number, number][] => {
    let prevLng = ring[0]?.[0]
    return ring.map(([lng, lat]) => {
        let adjustedLng = lng
        while (prevLng !== undefined && adjustedLng - prevLng > 180) adjustedLng -= 360
        while (prevLng !== undefined && adjustedLng - prevLng < -180) adjustedLng += 360
        prevLng = adjustedLng
        return [adjustedLng, lat]
    })
}

export const useAdsbAreaPreviewLayer = (enabled: boolean) => {
    const adsbEnabled = useAppSelector(selectAdsbEnabled)
    const center = useAppSelector(selectAdsbCircleCenter)

    return useMemo(() => {
        if (!adsbEnabled || !center || !enabled) return null

        const ring = unwrapAntimeridianRing(calculateCirclePolygon(center, ADSB_AREA_RADIUS_METERS))
        return new GeoJsonLayer<GeoJSON.GeoJsonProperties, PathStyleExtensionProps>({
            id: "adsb-area-preview",
            data: {
                type: "Feature",
                properties: {},
                geometry: { type: "Polygon", coordinates: [ring] },
            },
            stroked: true,
            filled: true,
            getLineColor: [39, 187, 255],
            getLineWidth: 2,
            lineWidthUnits: "pixels",
            getDashArray: DASHED,
            dashJustified: false,
            getFillColor: [39, 187, 255, 30],
            extensions: [new PathStyleExtension({ dash: true })],
            parameters: {
                depthCompare: "always",
            },
        })
    }, [adsbEnabled, center, enabled])
}
